import { Info, Sparkles } from "lucide-react";
import { PremiumStatusBadge } from "@/components/equinox/PremiumStatusBadge";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const methodologyBlocks = [
  {
    title: "Live store data",
    body: "App name, category, rating, review count, and store metadata are pulled live where available from supported sources.",
    variant: "live",
  },
  {
    title: "Estimated Equinox score",
    body: "ASO score, growth potential, lead score, sentiment labels, and some report commentary are generated from Equinox logic and weighted heuristics.",
    variant: "estimated",
  },
  {
    title: "Benchmark-based insight",
    body: "Competitor pressure, opportunity framing, and some recommendations are directional, based on benchmark logic rather than a live competitor intelligence API.",
    variant: "benchmark",
  },
];

const methodologyNotes = [
  "Use the report as a decision-support layer, not as a claim of 100% exact truth.",
  "The founder briefing is currently MOCKED and is not a real generated spokesperson video yet.",
  "The biggest value comes from combining the live inputs with operator review, not treating the output like a static scorecard.",
];

export const AsoMethodologyDialog = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-full border border-white/[0.1] bg-white/[0.04] px-4 py-2 text-sm text-white/82 transition-transform duration-300 hover:-translate-y-0.5 hover:border-blue-300/30 hover:bg-blue-400/10"
          data-testid="aso-methodology-dialog-trigger"
        >
          <Info className="h-4 w-4 text-blue-100" />
          How Equinox calculates this
        </button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className="max-h-[88vh] max-w-3xl overflow-y-auto rounded-[2rem] border border-white/[0.1] bg-[#060911] p-0 text-white shadow-[0_30px_120px_rgba(0,0,0,0.5)]"
        data-testid="aso-methodology-dialog"
      >
        <div className="noise-surface overflow-hidden rounded-[2rem]">
          <div className="border-b border-white/[0.08] bg-[radial-gradient(circle_at_top,rgba(37,99,235,0.18),transparent_35%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] px-6 py-6 lg:px-8">
            <DialogHeader>
              <p className="section-kicker">Scoring methodology</p>
              <DialogTitle className="mt-4 max-w-[15ch] font-display text-3xl tracking-[-0.04em] text-white lg:text-4xl">
                What is live, what is estimated, and what is benchmark-based.
              </DialogTitle>
            </DialogHeader>
            <p className="mt-5 max-w-2xl text-sm leading-8 text-white/[0.64]">
              We want the ASO Engine to feel premium and useful, but also transparent. This view explains the layers behind the numbers you see.
            </p>
          </div>

          <div className="space-y-6 px-6 py-6 lg:px-8">
            <div className="grid gap-4 md:grid-cols-3" data-testid="aso-methodology-blocks-grid">
              {methodologyBlocks.map((block, index) => (
                <div key={block.title} className="control-room-panel rounded-[1.5rem] p-5" data-testid={`aso-methodology-block-${index + 1}`}>
                  <PremiumStatusBadge label={block.title} variant={block.variant} />
                  <p className="mt-3 text-sm leading-7 text-white/[0.66]">{block.body}</p>
                </div>
              ))}
            </div>

            <div className="control-room-panel rounded-[1.8rem] p-5" data-testid="aso-methodology-notes-panel">
              <div className="flex items-center gap-2 text-blue-100">
                <Sparkles className="h-4 w-4" />
                <p className="text-[11px] uppercase tracking-[0.22em]">How to read the report</p>
              </div>
              <div className="mt-5 space-y-3">
                {methodologyNotes.map((note, index) => (
                  <div key={note} className="control-room-panel rounded-[1.2rem] px-4 py-3 text-sm leading-7 text-white/[0.68]" data-testid={`aso-methodology-note-${index + 1}`}>
                    {note}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <DialogClose asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.04] px-5 py-3 text-sm font-medium text-white/82 transition-transform duration-300 hover:-translate-y-0.5 hover:border-blue-300/30 hover:bg-blue-400/10"
                  data-testid="aso-methodology-dialog-close"
                >
                  Close
                </button>
              </DialogClose>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};